import clsx from 'clsx';
import type { CSSProperties, ComponentPropsWithoutRef } from 'react';

import { getColorStyle, getResponsiveClasses, isInlineColorValue } from '@commons/helpers/styles';
import type { ColorOrInline, ResponsiveStyle, Spacing } from '@commons/types/styles';

import styles from './index.module.scss';

/**
 * Type definition for the Separator component direction.
 */

export type SeparatorDirection = 'horizontal' | 'vertical';

/**
 * Type definition for the Separator component dash size.
 */

export type SeparatorDashSize = 'sm' | 'md' | 'lg';

/**
 * Type definition for the Separator component props.
 */

export type SeparatorProps = ComponentPropsWithoutRef<'hr'> & {
  /**
   * Whether it's a small separator (dash) or it takes the full width.
   */
  dash?: boolean;

  /**
   * The size of the dash.
   */
  dashSize?: SeparatorDashSize;

  /**
   * The background color of the separator.
   *
   * If a color is provided, it will override the default theme border color.
   */
  color?: ColorOrInline | 'primary';

  /**
   * The direction of the separator.
   *
   * @default 'horizontal'
   */
  direction?: ResponsiveStyle<SeparatorDirection>;

  /**
   * The margin top of the container.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <BaseContainer marginTop="md" />
   * <BaseContainer marginTop={20} />
   * <BaseContainer marginTop={{ xs: 'sm', lg: 'md' }} />
   */
  marginTop?: ResponsiveStyle<Spacing> | number;

  /**
   * The margin right of the container.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <BaseContainer marginRight="md" />
   * <BaseContainer marginRight={20} />
   * <BaseContainer marginRight={{ xs: 'sm', lg: 'md' }} />
   */
  marginRight?: ResponsiveStyle<Spacing> | number;

  /**
   * The margin bottom of the container.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <BaseContainer marginBottom="md" />
   * <BaseContainer marginBottom={20} />
   * <BaseContainer marginBottom={{ xs: 'sm', lg: 'md' }} />
   */
  marginBottom?: ResponsiveStyle<Spacing> | number;

  /**
   * The margin left of the container.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <BaseContainer marginLeft="md" />
   * <BaseContainer marginLeft={20} />
   * <BaseContainer marginLeft={{ xs: 'sm', lg: 'md' }} />
   */
  marginLeft?: ResponsiveStyle<Spacing> | number;
};

// Instead of repeating the margin props, we can loop through them.

const marginProps = ['marginTop', 'marginRight', 'marginBottom', 'marginLeft'] as const;

/**
 * Seperator component.
 *
 * An horizontal or vertical line with controllable color and margin.
 *
 * @see https://www.figma.com/design/1fysOYudJ918xtLcbZ3kJp/Crisis24-Website-2.0?node-id=4477-8303&node-type=frame&m=dev
 */

export const Separator = (props: SeparatorProps) => {
  const {
    color,
    direction = 'horizontal',
    dash,
    dashSize = 'lg',
    className,
    style,
    ...rest
  } = props;

  // Get responsive classes for direction.
  const responsiveClasses = [...getResponsiveClasses('direction', direction)];

  const inlineStyles: CSSProperties = {
    borderColor: getColorStyle(color),
    ...style,
  };

  // If a margin number is provided, we add it as an inline style.
  // Otherwise, we add a responsive class based on the value.
  // Finally, we remove the prop from the rest to avoid passing it to the hr.

  marginProps.forEach((prop) => {
    const value = props[prop];

    if (typeof value === 'number') {
      inlineStyles[prop] = `${value}px`;
    } else {
      const responsiveValue = getResponsiveClasses(prop, value);
      responsiveClasses.push(...responsiveValue);
    }

    delete rest[prop];
  });

  const classes = clsx(
    styles.separator,
    dash && styles.dash,
    dash && styles[`dash-${dashSize}`],
    styles[`direction-${direction}`],
    !color && styles.borderDefault,
    color && !isInlineColorValue(color) && `border-${color}`,
    responsiveClasses.map((className) => className),
    className
  );

  return <hr className={classes} style={inlineStyles} {...rest} />;
};

export default Separator;
